import { v4 as uuid4 } from 'uuid';
import { Point, Line } from '../../../context/context';

// OSM POI icon mapping
export const OSM_ICON_MAP: Record<string, string> = {
    'amenity=school': 'school',
    'amenity=hospital': 'hospital',
    'shop=supermarket': 'shopping',
    'leisure=playground': 'playground',
    'amenity=library': 'library',
    'amenity=restaurant': 'restaurant',
    'amenity=cafe': 'restaurant',
    'leisure=park': 'star',
    'amenity=bank': 'default',
    'amenity=post_office': 'default',
    'amenity=atm': 'default',
    'amenity=parking': 'parking',
    'amenity=police': 'flag',
    'amenity=fire_station': 'flag',
    'tourism=hotel': 'default',
    'tourism=museum': 'default',
    'amenity=pub': 'default',
    'amenity=bar': 'default',
    'amenity=pharmacy': 'hospital',
    'amenity=place_of_worship': 'default',
};

interface OSMElement {
    type: string;
    id: number;
    lat?: number;
    lon?: number;
    nodes?: number[];
    tags?: Record<string, string>;
    members?: Array<{
        type: string;
        ref: number;
        role: string;
    }>;
}

interface OSMData {
    elements: OSMElement[];
}

/**
 * Process OpenStreetMap bus route data
 */
export function processOSMBusData(data: OSMData): { points: Point[], lines: Line[] } {
    const points: Point[] = [];
    const lines: Line[] = [];

    // Process bus stops
    const stops = data.elements.filter(el => 
        el.type === 'node' && 
        el.tags && 
        (el.tags.highway === 'bus_stop' || el.tags.public_transport === 'platform')
    );

    for (const stop of stops) {
        if (stop.lat && stop.lon) {
            points.push({
                id: `osm-busstop-${stop.id}`,
                type: 'point',
                name: stop.tags?.name || 'Bus Stop',
                description: stop.tags?.operator || '',
                colour: '#1976d2',
                icon: 'bus',
                size: 24,
                isLocked: false,
                coordinates: [stop.lat, stop.lon],
            });
        }
    }

    // Process bus routes
    const relations = data.elements.filter(el => 
        el.type === 'relation' && 
        el.tags && 
        el.tags.type === 'route' && 
        el.tags.route === 'bus'
    );

    for (const rel of relations) {
        // Get all member ways and their nodes
        const wayIds = rel.members?.filter(m => m.type === 'way').map(m => m.ref) || [];
        const ways = data.elements.filter(el => el.type === 'way' && wayIds.includes(el.id));
        
        let coords: [number, number][] = [];
        for (const way of ways) {
            const wayNodes = way.nodes?.map(nid => {
                const n = data.elements.find(el => el.type === 'node' && el.id === nid);
                return n ? [n.lat, n.lon] : null;
            }).filter(Boolean) as [number, number][];
            
            if (wayNodes) {
                coords = coords.concat(wayNodes);
            }
        }

        if (coords.length > 1) {
            lines.push({
                id: `osm-busroute-${rel.id}`,
                type: 'line',
                name: rel.tags?.ref || rel.tags?.name || 'Bus Route',
                description: rel.tags?.operator || '',
                colour: '#388e3c',
                opacity: '0.8',
                borderColour: '#388e3c',
                borderWidth: 3,
                isLocked: false,
                coordinates: coords,
                arrowStyle: 'arrow',
            });
        }
    }

    return { points, lines };
} 